import {useEffect, useState, useRef} from 'react'
import util from "@utils/utils";
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom'; // ✅ 添加useSearchParams
import { v4 as uuidv4 } from 'uuid';
import {Eye,EyeOff,Smartphone,Lock,User} from 'react-feather'
import { useUser } from '../../hooks/useUser';
import { useConfig } from '../../contexts/ConfigContext';

export const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams(); // ✅ 获取URL参数
  const { user, login } = useUser();
  const { auth, clientId } = useConfig();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('qrcode');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [sceneStr, setSceneStr] = useState('');
  const pollingIntervalRef = useRef(null);
  const [formData, setFormData] = useState({
    user: '',
    password: ''
  });
  const [errors, setErrors] = useState({
    user: '',
    password: ''
  });
  // ✅ 登录成功后的处理函数
  const handleLoginSuccess = () => {
    const returnUrl = searchParams.get('returnUrl');
    if (returnUrl) {
      const decodedUrl = decodeURIComponent(returnUrl);
      console.log('登录成功，跳转到returnUrl:', decodedUrl);
      navigate(decodedUrl, { replace: true });
    } else {
      console.log('登录成功，跳转到首页');
      navigate('/policy', { replace: true });
    }
  };

  // ✅ 获取当前环境（移到组件顶部）
  const currentClientId = util.getCurrentClientId();

  // ✅ 修复：将所有useEffect移到条件判断之前
  useEffect(()=>{
    // ✅ a环境：直接跳转到首页，不显示登录页面
    if (currentClientId === 'client-a') {
      // 确保a环境有用户信息
      util.ensureUserInfo();
      navigate('/policy');
      return;
    }
    
    // ✅ b环境：检查是否已登录
    if(user && user.id){
      navigate('/policy');
      return;
    }
    
    // 根据配置选择登录方式
    if (auth.loginType === 'sso') {
      // SSO登录逻辑（仅b环境）
      handleSSOLogin();
    } else {
      // 本地登录逻辑 - 检查是否已有用户信息
      let localUserInfo = util.getStorageSafe(process.env.REACT_APP_LOCALSTORAGE_NAME);
      if (localUserInfo) {
        navigate('/policy');
      }
    }
  },[user, navigate, auth, currentClientId]) // 添加currentClientId到依赖

  // ✅ QR code相关的useEffect
  useEffect(() => {
    // 只有b环境才处理二维码逻辑
    if (currentClientId === 'client-a') {
      return;
    }
    
    if (activeTab === 'qrcode') {
      fetchQrCode();
    } else {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [activeTab, currentClientId]);

  // ✅ 修改SSO登录处理逻辑（仅适用于b环境）
  const handleSSOLogin = () => {
    // a环境不应该到达这里，但为了安全起见
    if (currentClientId === 'client-a') {
      navigate('/policy');
      return;
    }
    
    // 使用安全的localStorage检查方法
    let localUserInfo = util.getStorageSafe(process.env.REACT_APP_LOCALSTORAGE_NAME);
    
    // 检查是否有其他用户信息存储
    let zhengcetongUserInfo = util.getStorage('zhengcetong_user_info_v2');
    if(zhengcetongUserInfo && !localUserInfo){
      util.setStorage(process.env.REACT_APP_LOCALSTORAGE_NAME, zhengcetongUserInfo);
      localUserInfo = zhengcetongUserInfo;
    }
    
    if(process.env.REACT_APP_TYPE === 'grid-build'){
      // b环境：grid-build类型下不设置默认ID，重定向到登录页面
      window.location.href = `${process.env.REACT_APP_DOMAIN}/login`
    } else {
      let paramUserId = (new URL(window.location.href)).searchParams.get('u')
      if(localUserInfo){
        try{
          let userInfo = localUserInfo
          navigate('/policy');
        }catch(e){
          util.removeStorage(process.env.REACT_APP_LOCALSTORAGE_NAME)
          window.location.href = process.env.REACT_APP_DOMAIN
        }
      }else if(paramUserId){
        axios.post(`${process.env.REACT_APP_USER_INFO_API}/api/common/user/info`,{id:paramUserId}).then(resp =>{
          if(resp?.data?.code === '0'){
            util.setStorage(process.env.REACT_APP_LOCALSTORAGE_NAME,resp?.data?.content)
            window.location.href = process.env.REACT_APP_DOMAIN
          }else{
            window.alert('登录失败')
          }
        }).catch(e => {
          console.log(e)
          window.alert('登录失败')
        })
      }else{
        // b环境：如果没有用户信息，重定向到登录页面
        if(!localUserInfo){
          window.location.href = `${process.env.REACT_APP_DOMAIN}/login`
        }else{
          navigate('/policy');
        }
      }
    }
  }

  // ✅ 在组件开始就检查环境，a环境不渲染登录页面
  if (currentClientId === 'client-a') {
    return (
      <div className="hero min-h-screen bg-base-200">
        <div className="hero-content text-center">
          <div className="max-w-md">
            <h1 className="text-3xl font-bold pb-6">研究助理</h1>
            <p className="pb-6">正在跳转...</p>
            <span className="loading loading-spinner loading-lg"></span>
          </div>
        </div>
      </div>
    );
  }

  // QR code login functions（保持不变）
  const fetchQrCode = async () => {
    try {
      const apiUrl = auth.qrCodeApi || process.env.REACT_APP_OR_LOGIN_API_URL;
      if (!apiUrl) {
        console.error('QR Code API URL not configured');
        return;
      }
      
      const response = await axios.get(apiUrl, {
        withCredentials: true
      });
      
      if (response?.data?.code === "0") {
        setQrCodeUrl(response.data.content.qrCodeUrl);
        setSceneStr(response.data.content.sceneStr);
        startPolling(response.data.content.sceneStr);
      } else {
        console.error('Failed to get QR code:', response.data.message);
      }
    } catch (error) {
      console.error('Error fetching QR code:', error);
    }
  };

  const checkLoginStatus = async (sceneStrValue) => {
    try {
      const apiUrl = auth.qrCodeApi?.replace('/login', '/check') || process.env.REACT_APP_OR_CHECK_LOGIN_API_URL;
      if (!apiUrl) {
        console.error('QR Code check API URL not configured');
        return;
      }
      
      const response = await axios.get(apiUrl, {
        params: { sceneStr: sceneStrValue },
        withCredentials: true
      });
      
      if (response?.data?.code === "0") {
        const { openId } = response.data.content;
        if (openId) {
          clearInterval(pollingIntervalRef.current);
          login({id: openId});
        }
      }
    } catch (error) {
      console.error('Error checking login status:', error);
    }
  };

  const startPolling = (sceneStrValue) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(() => {
      checkLoginStatus(sceneStrValue);
    }, 2000);
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    if (!formData.user.trim()) {
      newErrors.user = '请输入账号';
      isValid = false;
    }

    if (!formData.password.trim()) {
      newErrors.password = '请输入密码';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const loginApiUrl = auth.localLoginApi || process.env.REACT_APP_LOGIN_API_URL;
    if (!loginApiUrl) {
      window.alert('登录API地址未配置，请联系管理员');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(loginApiUrl, {
        phoneNumber: formData.user,
        password: formData.password,
        targetValue: process.env.REACT_APP_DOMAIN,
        skipCaptchaCheck: true
      });

      if (response?.data?.code === '0') {
        const userData = {
          id: String(response?.data?.content),
          userId: String(response?.data?.content),
          phoneNumber: formData.user
        };
        
        const success = login(userData);
        if (!success) {
          window.alert('登录成功但状态保存失败，请重试');
        }
      } else {
        window.alert(response?.data?.message || '登录失败');
      }
    } catch (error) {
      console.error('Login error:', error);
      if (error.response) {
        window.alert(`登录失败: ${error.response.status} - ${error.response.data?.message || '未知错误'}`);
      } else if (error.request) {
        window.alert('登录失败: 无法连接到服务器，请检查网络连接');
      } else {
        window.alert('登录失败，请稍后重试');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 如果是SSO登录，显示加载状态
  if (auth.loginType === 'sso') {
    return (
      <div className="hero min-h-screen bg-base-200">
        <div className="hero-content text-center">
          <div className="max-w-md">
            <h1 className="text-3xl font-bold pb-6">研究助理</h1>
            <p className="pb-6">登录中...</p>
            <span className="loading loading-spinner loading-lg"></span>
          </div>
        </div>
      </div>
    );
  }

  // ✅ 只有b环境才显示登录表单
  return (
    <div
      className="hero min-h-screen"
      style={{
        backgroundImage: "url(https://img.daisyui.com/images/stock/photo-1481026469463-66327c86e544.webp)",
      }}>
      <div className="hero-overlay"></div>
      <div className="hero-content flex-col lg:flex-row-reverse">
        <div className="text-center lg:text-left lg:w-[50vw] lg:ml-12">
          <h1 className="text-5xl font-bold">欢迎使用政策通!</h1>
          <p className="py-6 text-2xl">
          政策经纬，一搜通达
          </p>
        </div>
        <div className="card bg-base-100 w-full max-w-xl shrink-0 shadow-2xl">
          <div className="card-body p-8 lg:p-12">
            <div className="mt-6 h-[350px] w-full relative">
              <div className={`absolute top-0 left-0 w-full h-full transition-opacity duration-300 opacity-100 z-10`}> 
                <div className="h-full flex flex-col justify-center">
                  <form className="space-y-4 w-full" onSubmit={handleSubmit}>
                    {/* 表单内容保持不变 */}
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text text-lg">账号：</span>
                      </label>
                      <label className="input input-bordered xl:input-lg flex items-center gap-2">
                        <User className="h-5 w-5 opacity-50" />
                        <input 
                          type="text" 
                          name="user"
                          value={formData.user}
                          onChange={handleInputChange}
                          className={`grow ${errors.user ? 'input-error' : ''}`} 
                          placeholder="请输入账号" 
                        />
                      </label>
                      {errors.user && (
                        <label className="label">
                          <span className="label-text-alt text-error">{errors.user}</span>
                        </label>
                      )}
                    </div>
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text text-lg">密码：</span>
                      </label>
                      <label className="input input-bordered xl:input-lg flex items-center gap-2">
                        <Lock className="h-5 w-5 opacity-50" />
                        <input 
                          type={showPassword ? "text" : "password"} 
                          name="password"
                          value={formData.password}
                          onChange={handleInputChange}
                          className={`grow ${errors.password ? 'input-error' : ''}`} 
                          placeholder="请输入密码" 
                        />
                        <button 
                          type="button"
                          className="btn btn-ghost btn-sm btn-square"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeOff className="h-5 w-5 opacity-50" />
                          ) : (
                            <Eye className="h-5 w-5 opacity-50" />
                          )}
                        </button>
                      </label>
                      {errors.password && (
                        <label className="label">
                          <span className="label-text-alt text-error">{errors.password}</span>
                        </label>
                      )}
                    </div>
                    <div className="form-control">
                      <label className="label text-center">
                        <a className="label-text-alt link link-hover text-lg text-secondary">忘记密码或无账号？联系管理员</a>
                      </label>
                    </div>
                    <div className="form-control mt-4">
                      <button 
                        type="submit" 
                        className="btn btn-primary xl:btn-lg w-full"
                        disabled={isLoading}
                      >
                        {isLoading&&<span className="loading loading-spinner"></span>}
                        {isLoading ? '登录中...' : '登录'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
